import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { ArrowLeft, User, Mail, Phone, Calendar, MapPin, Bell, Shield, Lock, Edit2, Save, Languages } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/hooks/useAuth';

interface ProfileSettingsProps {
  onBack?: () => void;
  userRole?: string;
}

export function ProfileSettings({ onBack, userRole = 'patient' }: ProfileSettingsProps) {
  // Get authenticated user data from API
  const { user } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.date_of_birth || '',
    address: user?.address || '',
    emergencyContact: user?.emergency_contact || '',
    language: user?.language || 'english',
  });

  const [notifications, setNotifications] = useState({
    exerciseReminders: true,
    appointmentAlerts: true,
    medicationReminders: true,
    progressUpdates: true,
    messageNotifications: true,
    emailNotifications: false,
  });

  const handleSaveProfile = () => {
    setIsEditing(false);
    toast.success('Profile updated successfully!', {
      description: 'Your changes have been saved',
    });
  };

  const handleNotificationChange = (key: keyof typeof notifications) => {
    setNotifications({ ...notifications, [key]: !notifications[key] });
    toast.info('Notification preferences updated');
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-primary-foreground">Settings</h1>
            <p className="text-primary-foreground/80">Manage your profile and preferences</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Profile Header */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
                <span className="text-primary-foreground">
                  {profileData.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-primary">{profileData.name}</h2>
                <p className="text-muted-foreground capitalize">{userRole}</p>
                <p className="text-muted-foreground">{profileData.email}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Settings Tabs */}
        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-primary" />
                    Full Name
                  </Label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Phone Number
                  </Label>
                  <Input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={profileData.dateOfBirth}
                    onChange={(e) => setProfileData({ ...profileData, dateOfBirth: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    Address
                  </Label>
                  <Input
                    value={profileData.address}
                    onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                <Separator />

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-accent" />
                    Emergency Contact
                  </Label>
                  <Input
                    type="tel"
                    value={profileData.emergencyContact}
                    onChange={(e) => setProfileData({ ...profileData, emergencyContact: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>

                {isEditing && (
                  <Button 
                    className="w-full bg-accent hover:bg-accent/90"
                    onClick={handleSaveProfile}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary">Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Languages className="w-4 h-4 text-primary" />
                    Language
                  </Label>
                  <Select value={profileData.language} onValueChange={(val) => setProfileData({ ...profileData, language: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="hindi">Hindi</SelectItem>
                      <SelectItem value="tamil">Tamil</SelectItem>
                      <SelectItem value="telugu">Telugu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Exercise Reminders</p>
                    <p className="text-muted-foreground">Get notified about daily exercises</p>
                  </div>
                  <Switch
                    checked={notifications.exerciseReminders}
                    onCheckedChange={() => handleNotificationChange('exerciseReminders')}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Appointment Alerts</p>
                    <p className="text-muted-foreground">Reminders for upcoming appointments</p>
                  </div>
                  <Switch
                    checked={notifications.appointmentAlerts}
                    onCheckedChange={() => handleNotificationChange('appointmentAlerts')}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Medication Reminders</p>
                    <p className="text-muted-foreground">Never miss your medications</p>
                  </div>
                  <Switch
                    checked={notifications.medicationReminders}
                    onCheckedChange={() => handleNotificationChange('medicationReminders')}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Progress Updates</p>
                    <p className="text-muted-foreground">Weekly progress summaries</p>
                  </div>
                  <Switch
                    checked={notifications.progressUpdates}
                    onCheckedChange={() => handleNotificationChange('progressUpdates')}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Message Notifications</p>
                    <p className="text-muted-foreground">Chat messages from healthcare team</p>
                  </div>
                  <Switch
                    checked={notifications.messageNotifications}
                    onCheckedChange={() => handleNotificationChange('messageNotifications')}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Email Notifications</p>
                    <p className="text-muted-foreground">Receive updates via email</p>
                  </div>
                  <Switch
                    checked={notifications.emailNotifications}
                    onCheckedChange={() => handleNotificationChange('emailNotifications')}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Security Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => toast.info('Password change functionality coming soon')}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </Button>

                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => toast.info('2FA setup coming soon')}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Enable Two-Factor Authentication
                </Button>

                <Separator />

                <div>
                  <h3 className="text-primary mb-2">Active Sessions</h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-secondary rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-foreground">Chrome on Windows</p>
                          <p className="text-muted-foreground">Current session • Chennai, India</p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-destructive">
                          Revoke
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <Button 
                  variant="outline" 
                  className="w-full text-destructive border-destructive hover:bg-destructive/10"
                  onClick={() => toast.warning('This will delete all your data permanently')}
                >
                  Delete Account
                </Button>
              </CardContent>
            </Card>

            <Card className="shadow-lg bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-primary mb-1">Data Privacy</h3>
                    <p className="text-muted-foreground">
                      Your health data is encrypted and securely stored. We comply with HIPAA and GDPR regulations.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}